import {
    computed,
    inject,
    runInInjectionContext,
    type Injector,
    type Signal,
} from '@angular/core';
import {
    assertInjector,
    type QueryStatus,
    type ResolvedProblemDetails,
} from '@quipex/core/utils';
import type { CurrentUserBuildingPermissionResponse } from '@quipex/shared/contracts';
import type { Observable } from 'rxjs';
import { filter } from 'rxjs';
import {
    hasBuildingPermission,
    type HasBuildingPermissionOptions,
    type HasPermissionType,
} from '../permissions';
import { UserBuildingStore } from './user-building.store';

export interface InjectUserBuilding {
    (): CurrentUserBuildingPermissionResponse | null;
    $: Observable<CurrentUserBuildingPermissionResponse | null>;
    required$: Observable<CurrentUserBuildingPermissionResponse>;

    status: Signal<QueryStatus>;
    isIdle: Signal<boolean>;
    isLoading: Signal<boolean>;
    isSuccess: Signal<boolean>;
    isError: Signal<boolean>;
    isFetched: Signal<boolean>;
    error: Signal<ResolvedProblemDetails | null>;

    required: () => CurrentUserBuildingPermissionResponse;
    hasPermission: (
        permissions: HasPermissionType,
        options?: HasBuildingPermissionOptions
    ) => Signal<boolean>;
}

export function injectUserBuilding(injector?: Injector): InjectUserBuilding {
    const resolvedInjector = assertInjector(injectUserBuilding, injector);

    const userBuildingStore = runInInjectionContext(resolvedInjector, () => {
        return inject(UserBuildingStore);
    });

    const buildingPermission = userBuildingStore.buildingPermission;

    const required = () => {
        const permission = buildingPermission();
        if (!permission) {
            throw new TypeError(
                'Building permission is either not loaded or not found'
            );
        }

        return permission;
    };

    const hasPermissionFn = (
        permissions: HasPermissionType,
        options: HasBuildingPermissionOptions = {}
    ) =>
        computed(() => {
            const permission = buildingPermission();
            return hasBuildingPermission(permission, permissions, {
                buildingId:
                    options.buildingId ?? userBuildingStore.currentBuildingId(),
                organisationId: options.organisationId,
                userId: options.userId,
            });
        });

    const fn = (() => buildingPermission()) as InjectUserBuilding;
    fn.$ = userBuildingStore.buildingPermission$;
    fn.required$ = userBuildingStore.buildingPermission$.pipe(
        filter(
            (permission): permission is CurrentUserBuildingPermissionResponse =>
                permission !== null
        )
    );
    fn.status = userBuildingStore.buildingPermissionQuery.status;
    fn.isIdle = userBuildingStore.buildingPermissionQuery.isIdle;
    fn.isLoading = userBuildingStore.buildingPermissionQuery.isLoading;
    fn.isSuccess = userBuildingStore.buildingPermissionQuery.isSuccess;
    fn.isError = userBuildingStore.buildingPermissionQuery.isError;
    fn.isFetched = userBuildingStore.buildingPermissionQuery.isFetched;
    fn.error = userBuildingStore.buildingPermissionQuery.error;
    fn.required = required;
    fn.hasPermission = hasPermissionFn;
    return fn;
}
